import { useState, useEffect } from 'react';
import axios from 'axios';
import { Truck, Plus, PackageCheck, AlertTriangle, Filter, X } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { PaginationControls } from '@/components/ui/pagination-controls';
import { isAdmin } from '@/utils/permissions';
import { getSessionItem } from '@/utils/sessionManager';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DispatchView = () => {
  const [dispatches, setDispatches] = useState([]);
  const [items, setItems] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [inventory, setInventory] = useState([]);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [creating, setCreating] = useState(false);
  const [customerFilter, setCustomerFilter] = useState('all');
  const [prevWorkingDay, setPrevWorkingDay] = useState(() => {
    const yest = new Date();
    yest.setDate(yest.getDate() - 1);
    return yest.toISOString().split('T')[0];
  });
  
  // Pagination state
  const [pagination, setPagination] = useState({
    page: 1,
    pageSize: 50,
    totalCount: 0,
    totalPages: 0
  });
  
  const [newDispatch, setNewDispatch] = useState({
    invoice_number: '',
    customer_id: '',
    item_id: '',
    dispatch_date: new Date().toISOString().split('T')[0],
    qty: 0,
    warehouse_id: '',
    status: 'DISPATCHED'
  });

  useEffect(() => {
    fetchMasterData();
  }, []);

  useEffect(() => {
    fetchDispatches();
  }, [pagination.page, customerFilter]);

  const fetchMasterData = async () => {
    try {
      const [itemRes, customerRes, whRes, invRes] = await Promise.all([
        axios.get(`${API}/items`),
        axios.get(`${API}/customers`),
        axios.get(`${API}/warehouses`),
        axios.get(`${API}/inventory`)
      ]);
      setItems(itemRes.data);
      setCustomers(customerRes.data);
      setWarehouses(whRes.data);
      setInventory(invRes.data);
    } catch (error) {
      console.error('Error fetching master data:', error);
    }
  };

  const fetchDispatches = async () => {
    try {
      const params = new URLSearchParams({
        page: pagination.page.toString(),
        page_size: pagination.pageSize.toString(),
        sort_by: 'dispatch_date',
        sort_order: 'desc'
      });
      
      if (customerFilter && customerFilter !== 'all') {
        params.append('customer_id', customerFilter);
      }
      
      const response = await axios.get(`${API}/dispatch?${params.toString()}`);
      
      if (response.data.data) {
        setDispatches(response.data.data);
        setPagination(prev => ({
          ...prev,
          totalCount: response.data.pagination.total_count,
          totalPages: response.data.pagination.total_pages
        }));
      } else {
        setDispatches(response.data);
      }
    } catch (error) {
      console.error('Error fetching dispatches:', error);
      toast.error('Failed to load dispatch data');
    }
  };

  const handlePageChange = (newPage) => {
    setPagination(prev => ({ ...prev, page: newPage }));
  };

  const getItemInfo = (id) => {
    const item = items.find(i => i.id === id);
    return item ? `${item.part_number} - ${item.description}` : id;
  };

  const getCustomerName = (id) => customers.find(c => c.id === id)?.name || 'N/A';
  const getWarehouseName = (id) => warehouses.find(w => w.id === id)?.name || 'N/A';

  const getAvailableQty = (itemId, warehouseId) => {
    const inv = inventory.find(i => i.item_id === itemId && i.warehouse_id === warehouseId);
    return inv ? inv.qty : 0;
  };

  // Compute previous working day using customer holiday calendar (for non-admin)
  const computePrevWorkingDay = async (customerId) => {
    if (isAdmin()) return; // Admin has no date restrictions
    if (!customerId) {
      // No customer selected yet — fallback to yesterday
      const yest = new Date(); yest.setDate(yest.getDate() - 1);
      setPrevWorkingDay(yest.toISOString().split('T')[0]);
      return;
    }
    try {
      const res = await axios.get(`${API}/customer-calendar/${customerId}`);
      const holidays = new Set((res.data || []).filter(c => c.is_holiday).map(c => c.date));
      const d = new Date();
      d.setDate(d.getDate() - 1);
      for (let i = 0; i < 14; i++) {
        const ds = d.toISOString().split('T')[0];
        if (!holidays.has(ds)) { setPrevWorkingDay(ds); return; }
        d.setDate(d.getDate() - 1);
      }
    } catch {
      // fallback to yesterday
    }
    const yest = new Date(); yest.setDate(yest.getDate() - 1);
    setPrevWorkingDay(yest.toISOString().split('T')[0]);
  };

  // Get FG items filtered by selected customer in create dialog
  const getFilteredFGItems = () => {
    const fgItems = items.filter(i => i.category === 'FG');
    if (newDispatch.customer_id) {
      return fgItems.filter(i => i.customer_id === newDispatch.customer_id || !i.customer_id);
    }
    return fgItems;
  };

  const handleCreateDispatch = async () => {
    if (!newDispatch.invoice_number || !newDispatch.customer_id || !newDispatch.item_id || 
        !newDispatch.warehouse_id || newDispatch.qty <= 0) {
      toast.error('Please fill all required fields');
      return;
    }

    // Date restriction: non-admin can only dispatch for today or previous working day
    if (!isAdmin()) {
      const todayStr = new Date().toISOString().split('T')[0];
      const allowedDates = [todayStr, prevWorkingDay].filter(Boolean);
      if (!allowedDates.includes(newDispatch.dispatch_date)) {
        toast.error(
          prevWorkingDay
            ? `Dispatch date must be today (${todayStr}) or previous working day (${prevWorkingDay})`
            : `Dispatch date must be today (${todayStr})`
        );
        return;
      }
    }

    // Check available quantity
    const availableQty = getAvailableQty(newDispatch.item_id, newDispatch.warehouse_id);
    if (availableQty < newDispatch.qty) {
      toast.error(`Insufficient inventory! Available: ${availableQty}, Requested: ${newDispatch.qty}`);
      return;
    }

    setCreating(true);
    try {
      const sessionToken = getSessionItem('sessionToken');
      const response = await axios.post(`${API}/dispatch/create`, newDispatch, {
        headers: { 'x-session-token': sessionToken || '' }
      });
      toast.success('Dispatch created successfully with FIFO inventory consumption');
      
      if (response.data.bom_consumed && Object.keys(response.data.bom_consumed).length > 0) {
        const bomDetails = Object.entries(response.data.bom_consumed)
          .map(([itemId, qty]) => `${getItemInfo(itemId)}: ${qty}`)
          .join(', ');
        toast.info(`BOM consumed: ${bomDetails}`, { duration: 5000 });
      }
      
      setShowCreateDialog(false);
      setNewDispatch({
        invoice_number: '',
        customer_id: '',
        item_id: '',
        dispatch_date: new Date().toISOString().split('T')[0],
        qty: 0,
        warehouse_id: '',
        status: 'DISPATCHED'
      });
      
      fetchDispatches();
      fetchMasterData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create dispatch');
    } finally {
      setCreating(false);
    }
  };

  const openCreateDialog = async () => {
    try {
      const invRes = await axios.get(`${API}/inventory`);
      setInventory(invRes.data);
    } catch (error) {
      console.error('Error refreshing inventory:', error);
    }
    const invoiceNum = `INV-${Date.now().toString().slice(-6)}`;
    const today = new Date().toISOString().split('T')[0];
    setNewDispatch({ ...newDispatch, invoice_number: invoiceNum, dispatch_date: today });
    setPrevWorkingDay(null);
    setShowCreateDialog(true);
  };

  // Group dispatches by date
  const groupedDispatches = dispatches.reduce((acc, dispatch) => {
    const date = dispatch.dispatch_date;
    if (!acc[date]) acc[date] = [];
    acc[date].push(dispatch);
    return acc;
  }, {});

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold">Dispatch & Invoice</h2>
          <p className="text-gray-500">Create dispatch orders with FIFO inventory consumption</p>
        </div>
        <Button onClick={openCreateDialog} data-testid="create-dispatch-btn">
          <Plus className="mr-2 h-4 w-4" />
          Create Dispatch
        </Button>
      </div>

      {/* Customer Filter */}
      <div className="bg-slate-50 border border-slate-200 rounded-lg p-4">
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <Filter className="w-4 h-4 text-slate-600" />
            <span className="text-sm font-medium text-slate-700">Filter by Customer:</span>
          </div>
          <Select
            data-testid="dispatch-customer-filter"
            value={customerFilter}
            onValueChange={(value) => {
              setCustomerFilter(value);
              setPagination(prev => ({ ...prev, page: 1 }));
            }}
          >
            <SelectTrigger className="w-[250px] h-9">
              <SelectValue placeholder="All customers" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All customers</SelectItem>
              {customers.map(c => (
                <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          {customerFilter !== 'all' && (
            <Button variant="outline" size="sm" onClick={() => setCustomerFilter('all')} data-testid="clear-dispatch-filter">
              <X className="w-4 h-4 mr-1" /> Clear
            </Button>
          )}
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-gray-500">Total Dispatches</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold" data-testid="total-dispatches-count">{pagination.totalCount}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-gray-500">Total Quantity Dispatched</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {dispatches.reduce((sum, d) => sum + d.qty, 0)}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-gray-500">Unique Customers</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {new Set(dispatches.map(d => d.customer_id)).size}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Dispatch Records by Date */}
      {Object.keys(groupedDispatches).length === 0 ? (
        <Card>
          <CardContent className="py-10 text-center">
            <Truck className="h-12 w-12 mx-auto text-gray-400 mb-4" />
            <p className="text-gray-500">No dispatches found</p>
          </CardContent>
        </Card>
      ) : (
        Object.entries(groupedDispatches)
          .sort(([dateA], [dateB]) => dateB.localeCompare(dateA))
          .map(([date, dateDispatches]) => (
            <Card key={date}>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Truck className="h-5 w-5" />
                  Dispatches on {date}
                </CardTitle>
                <CardDescription>
                  {dateDispatches.length} dispatch{dateDispatches.length !== 1 ? 'es' : ''} | 
                  Total Qty: {dateDispatches.reduce((sum, d) => sum + d.qty, 0)}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Posting Date</TableHead>
                      <TableHead>Invoice Number</TableHead>
                      <TableHead>Customer</TableHead>
                      <TableHead>Item</TableHead>
                      <TableHead>Quantity</TableHead>
                      <TableHead>Warehouse</TableHead>
                      <TableHead>Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {dateDispatches.map((dispatch) => (
                      <TableRow key={dispatch.id} data-testid={`dispatch-row-${dispatch.id}`}>
                        <TableCell className="text-xs text-slate-600 whitespace-nowrap">
                          {dispatch.created_ts ? new Date(dispatch.created_ts).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: true }) : '-'}
                        </TableCell>
                        <TableCell className="font-medium">{dispatch.invoice_number}</TableCell>
                        <TableCell>{getCustomerName(dispatch.customer_id)}</TableCell>
                        <TableCell>{getItemInfo(dispatch.item_id)}</TableCell>
                        <TableCell>{dispatch.qty}</TableCell>
                        <TableCell>{getWarehouseName(dispatch.warehouse_id)}</TableCell>
                        <TableCell>
                          <Badge className="bg-green-100 text-green-800">
                            {dispatch.status}
                          </Badge>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          ))
      )}
      
      {/* Pagination Controls */}
      <PaginationControls pagination={pagination} onPageChange={handlePageChange} />

      {/* Create Dispatch Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Create New Dispatch</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-4 py-4">
            <div>
              <Label htmlFor="invoice_number">Invoice Number *</Label>
              <Input
                id="invoice_number"
                data-testid="dispatch-invoice-input"
                value={newDispatch.invoice_number}
                onChange={(e) => setNewDispatch({ ...newDispatch, invoice_number: e.target.value })}
                placeholder="INV-001"
              />
            </div>
            <div>
              <Label htmlFor="dispatch_date">Dispatch Date *</Label>
              <Input
                id="dispatch_date"
                data-testid="dispatch-date-input"
                type="date"
                value={newDispatch.dispatch_date}
                onChange={(e) => setNewDispatch({ ...newDispatch, dispatch_date: e.target.value })}
                min={!isAdmin() ? (prevWorkingDay || new Date().toISOString().split('T')[0]) : undefined}
                max={!isAdmin() ? new Date().toISOString().split('T')[0] : undefined}
              />
              {!isAdmin() && (
                <p className="text-xs text-slate-500 mt-1">
                  Allowed: today{prevWorkingDay ? ` or previous working day (${prevWorkingDay})` : ''}
                </p>
              )}
            </div>
            <div>
              <Label htmlFor="customer">Customer *</Label>
              <Select 
                value={newDispatch.customer_id} 
                onValueChange={(val) => {
                  setNewDispatch({ ...newDispatch, customer_id: val, item_id: '' });
                  computePrevWorkingDay(val);
                }}
              >
                <SelectTrigger data-testid="dispatch-customer-select">
                  <SelectValue placeholder="Select customer" />
                </SelectTrigger>
                <SelectContent>
                  {customers.map(c => (
                    <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="warehouse">Warehouse *</Label>
              <Select 
                value={newDispatch.warehouse_id} 
                onValueChange={(val) => setNewDispatch({ ...newDispatch, warehouse_id: val })}
              >
                <SelectTrigger data-testid="dispatch-warehouse-select">
                  <SelectValue placeholder="Select warehouse" />
                </SelectTrigger>
                <SelectContent>
                  {warehouses.map(w => (
                    <SelectItem key={w.id} value={w.id}>{w.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="col-span-2">
              <Label htmlFor="item">Item (FG) * {newDispatch.customer_id ? `- Showing items for ${getCustomerName(newDispatch.customer_id)}` : ''}</Label>
              <Select 
                value={newDispatch.item_id} 
                onValueChange={(val) => setNewDispatch({ ...newDispatch, item_id: val })}
              >
                <SelectTrigger data-testid="dispatch-item-select">
                  <SelectValue placeholder={newDispatch.customer_id ? "Select item" : "Select customer first"} />
                </SelectTrigger>
                <SelectContent>
                  {getFilteredFGItems().map(i => (
                    <SelectItem key={i.id} value={i.id}>
                      {i.part_number} - {i.description}
                      {newDispatch.warehouse_id && (
                        <span className="ml-2 text-xs text-gray-500">
                          (Available: {getAvailableQty(i.id, newDispatch.warehouse_id)})
                        </span>
                      )}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="qty">Quantity *</Label>
              <Input
                id="qty"
                data-testid="dispatch-qty-input"
                type="number"
                value={newDispatch.qty}
                onChange={(e) => setNewDispatch({ ...newDispatch, qty: parseFloat(e.target.value) || 0 })}
                placeholder="0"
              />
              {newDispatch.item_id && newDispatch.warehouse_id && (
                <p className="text-sm text-gray-500 mt-1">
                  Available: {getAvailableQty(newDispatch.item_id, newDispatch.warehouse_id)}
                </p>
              )}
            </div>
          </div>
          {newDispatch.item_id && newDispatch.warehouse_id && 
           getAvailableQty(newDispatch.item_id, newDispatch.warehouse_id) < newDispatch.qty && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-md p-3 flex items-start gap-2">
              <AlertTriangle className="h-5 w-5 text-yellow-600 mt-0.5" />
              <div className="text-sm text-yellow-800">
                <p className="font-medium">Insufficient Inventory</p>
                <p>
                  Available: {getAvailableQty(newDispatch.item_id, newDispatch.warehouse_id)} | 
                  Requested: {newDispatch.qty}
                </p>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateDialog(false)}>Cancel</Button>
            <Button onClick={handleCreateDispatch} disabled={creating} data-testid="submit-dispatch-btn">
              {creating ? 'Creating...' : 'Create Dispatch'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DispatchView;
